<template>
  <div
    class="vc-section vc-card vc-card--compact vc-interrupt"
    :class="{
      'vc-interrupt--collapsed': !settings || !settings.enabled,
      'is-disabled': disabled,
    }"
  >
    <div class="vc-interrupt__head">
      <span class="vc-interrupt__title">打断设置</span>
      <div v-if="settings" class="vc-interrupt__switch">
        <span class="vc-interrupt__switch-label">{{ settings.enabled ? '开启' : '关闭' }}</span>
        <i-switch v-model="settings.enabled" :disabled="disabled" />
      </div>
    </div>

    <div v-show="settings && settings.enabled" class="vc-interrupt__body">
      <div class="vc-interrupt-option vc-interrupt-option--inline">
        <div class="vc-interrupt-option__head">
          <Checkbox v-model="settings.consecutive_recognition_enabled" :disabled="disabled">连续识别打断</Checkbox>
          <span class="vc-interrupt-chars">
            <span>识别到</span>
            <InputNumber
              v-model="settings.consecutive_recognition_chars"
              :min="consecutiveCharsMin"
              :max="consecutiveCharsMax"
              :precision="0"
              :disabled="disabled || !settings.consecutive_recognition_enabled"
              class="vc-interrupt-chars__input"
              @on-change="onConsecutiveCharsChange"
            />
            <span>个字即打断</span>
          </span>
        </div>
      </div>

      <div class="vc-interrupt-option">
        <div class="vc-interrupt-option__head">
          <Checkbox v-model="settings.filler_words_enabled" :disabled="disabled">语气词不打断</Checkbox>
          <Tooltip
            content="如果同时勾选连续识别打断，识别到 N 个字即打断，如果设置了语气词不打断，则语气词不打断优先判断。"
            placement="top"
            :max-width="320"
            transfer
          >
            <Icon type="ios-help-circle-outline" class="vc-interrupt-help" />
          </Tooltip>
        </div>
        <KeywordTagEditor
          v-show="settings.filler_words_enabled"
          :value="settings.filler_words"
          :disabled="disabled"
          placeholder="回车添加，例如：嗯、嗯嗯、嗯嗯嗯、好的、对对"
          @input="onListInput('filler_words', $event)"
        />
      </div>

      <div class="vc-interrupt-option">
        <div class="vc-interrupt-option__head">
          <Checkbox v-model="settings.utterance_lock_enabled" :disabled="disabled">AI 语句不打断</Checkbox>
          <Tooltip
            content="AI 当前语句含保护词时，不允许被打断。"
            placement="top"
            :max-width="320"
            transfer
          >
            <Icon type="ios-help-circle-outline" class="vc-interrupt-help" />
          </Tooltip>
        </div>
        <div v-show="settings.utterance_lock_enabled" class="vc-interrupt-protect__box">
          <div class="vc-interrupt-field">
            <div class="vc-interrupt-field__label">保护词</div>
            <KeywordTagEditor
              :value="settings.lock_words"
              :disabled="disabled"
              placeholder="回车添加，例如：价格、手机号、验证码"
              @input="onListInput('lock_words', $event)"
            />
          </div>
          <div class="vc-interrupt-field vc-interrupt-field--last">
            <div class="vc-interrupt-field__label">强制打断词（选填）</div>
            <KeywordTagEditor
              :value="settings.force_interrupt_words"
              :disabled="disabled"
              placeholder="回车添加，例如：等一下、我不需要"
              @input="onListInput('force_interrupt_words', $event)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import KeywordTagEditor from '../shared/KeywordTagEditor.vue'
import {
  CONSECUTIVE_RECOGNITION_CHARS_DEFAULT,
  CONSECUTIVE_RECOGNITION_CHARS_MAX,
  CONSECUTIVE_RECOGNITION_CHARS_MIN,
  createDefaultInterruptSettings,
  ensureInterruptSettings,
  normalizeConsecutiveRecognitionChars,
} from '../../utils/callControlHelpers'

export default {
  name: 'InterruptSettingsSection',
  components: { KeywordTagEditor },
  props: {
    callControl: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    settings() {
      return this.callControl.interrupt_settings
    },
    consecutiveCharsMin() {
      return CONSECUTIVE_RECOGNITION_CHARS_MIN
    },
    consecutiveCharsMax() {
      return CONSECUTIVE_RECOGNITION_CHARS_MAX
    },
  },
  mounted() {
    this.ensureShape()
  },
  methods: {
    ensureShape() {
      if (!this.callControl.interrupt_settings || typeof this.callControl.interrupt_settings !== 'object') {
        this.$set(this.callControl, 'interrupt_settings', createDefaultInterruptSettings())
      }
      ensureInterruptSettings(this.callControl)
      const s = this.callControl.interrupt_settings
      ;[
        'consecutive_recognition_enabled',
        'consecutive_recognition_chars',
        'filler_words_enabled',
      ].forEach((key) => {
        this.$set(s, key, s[key])
      })
    },
    onListInput(key, list) {
      if (this.disabled || !this.settings) return
      const next = Array.isArray(list) ? [...list] : []
      this.$set(this.settings, key, next)
      if (key === 'force_interrupt_words') {
        this.$set(this.settings, 'force_interrupt_enabled', next.length > 0)
      }
    },
    onConsecutiveCharsChange(value) {
      if (this.disabled || !this.settings) return
      const next = normalizeConsecutiveRecognitionChars(
        value == null ? CONSECUTIVE_RECOGNITION_CHARS_DEFAULT : value,
      )
      if (this.settings.consecutive_recognition_chars !== next) {
        this.$set(this.settings, 'consecutive_recognition_chars', next)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './shared.scss';

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-card--compact {
  padding-bottom: 16px;
}

.vc-interrupt--collapsed {
  padding-bottom: 16px;

  .vc-interrupt__head {
    margin-bottom: 0;
  }
}

.vc-interrupt.is-disabled {
  .vc-interrupt__title,
  .vc-interrupt__switch-label {
    color: #bfbfbf;
  }

  .vc-interrupt__switch,
  .vc-interrupt__body {
    opacity: 0.45;
    pointer-events: none;
  }
}

.vc-interrupt__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 24px;
  margin-bottom: 16px;
}

.vc-interrupt__title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  line-height: 24px;
}

.vc-interrupt__switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 24px;
}

.vc-interrupt__switch-label {
  font-size: 13px;
  color: #595959;
  line-height: 24px;
}

.vc-interrupt__body {
  text-align: left;
}

.vc-interrupt-help {
  margin-left: 4px;
  color: #8c8c8c;
  cursor: help;
  font-size: 16px;
}

.vc-interrupt-option {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  &--inline .vc-interrupt-option__head {
    margin-bottom: 0;
  }

  &__head {
    display: flex;
    align-items: center;
    min-height: 32px;
    margin-bottom: 10px;
  }
}

.vc-interrupt-chars {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
  color: #262626;
  font-size: 14px;
  line-height: 22px;

  &__input {
    width: 56px;
  }
}

::v-deep .vc-interrupt-chars__input.ivu-input-number {
  height: 32px;
  border-radius: 4px;

  .ivu-input-number-handler-wrap {
    display: none;
  }

  .ivu-input-number-input-wrap {
    height: 32px;
  }

  .ivu-input-number-input {
    height: 32px;
    text-align: center;
  }
}

::v-deep .vc-interrupt-option .ivu-checkbox-wrapper {
  margin-right: 0;
  font-size: 14px;
  color: #262626;
}

.vc-interrupt-field {
  margin-bottom: 16px;

  &--last {
    margin-bottom: 0;
  }

  &__label {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #262626;
    line-height: 20px;
  }
}

.vc-interrupt-protect__box {
  padding: 16px;
  border: 1px solid #e6ebf5;
  border-radius: 8px;
  background: #f7f9fc;
}
</style>
